import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig"; 
import { useProjects } from "./ProjectContext";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const TaskContext = createContext();

export const TaskProvider = ({ children }) => {
  const { updateProject } = useProjects();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, inProgress: 0, in_progress: 0, completed: 0, byStatus: {} });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchTasks = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const res = await api.get("/tasks", { params });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tasks || res.data?.data || []);
      if (Array.isArray(list)) {
        setTasks(list);
      }
      if (res.data?.counts) {
        setCounts(res.data.counts);
      }
    } catch (err) {
      console.error("Error loading tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🟢 Dedicated Fetch single task (View & Edit) - Stable Callback
  const fetchTaskById = useCallback(async (id) => {
    try {
      const res = await api.get(`/tasks/${id}`);
      return res.data?.data || res.data?.task || res.data;
    } catch (err) {
      console.error("Single task fetch error:", err);
      return null;
    }
  }, []);

  // 🟢 Fetch filter options (projects, statuses, priorities) dynamically from server
  const fetchTaskFilters = useCallback(async () => {
    try {
      const res = await api.get("/tasks/filters");
      return res.data?.data || null;
    } catch (err) {
      console.error("Error fetching task filters:", err);
      return null;
    }
  }, []);

  // 🟢 Dedicated GET API for Task creation dropdowns (Projects & Employees)
  const fetchTaskFormOptions = useCallback(async () => {
    try {
      const res = await api.get("/tasks/form-options");
      if (res.data?.success && res.data?.data) {
        return res.data.data;
      }
      return res.data || null;
    } catch (err) {
      console.error("Error fetching task form options:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const addTask = async (taskData) => {
    try {
      const res = await api.post("/tasks", taskData);
      await fetchTasks();
      toast.success("Task created successfully");
      if (taskData.projectId) updateProjectProgress(taskData.projectId);
      return res.data?.id || res.data?.task?.id || true;
    } catch (err) {
      console.error("Error adding task:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || "Failed to add task";
      toast.error(errMsg);
      return false;
    }
  };

  const updateTask = async (id, updatedData) => {
    try {
      const res = await api.put(`/tasks/${id}`, updatedData);
      const serverUpdated = res.data?.task || res.data?.data || updatedData;
      setTasks(prev => prev.map(t => String(t.id) === String(id) ? { ...t, ...serverUpdated } : t));
      if (updatedData.projectId) updateProjectProgress(updatedData.projectId);
      toast.success("Task updated successfully");
      return true;
    } catch (err) {
      console.error("Task update error:", err);
      toast.error(err.response?.data?.error || "Failed to update task");
      return false;
    }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks(prev => prev.filter(t => String(t.id) !== String(id)));
      setCounts(prev => ({
        ...prev,
        total: Math.max(0, (prev?.total || 1) - 1)
      }));
      toast.success("Task deleted successfully");
      return true;
    } catch (err) {
      console.error("Task delete error:", err);
      toast.error(err.response?.data?.error || "Failed to delete task");
      return false;
    }
  };

  const getTaskById = (id) => {
    return tasks.find((t) => String(t.id) === String(id));
  };

  const updateProjectProgress = (projectId) => {
    const projectTasks = tasks.filter(t => String(t.projectId) === String(projectId));
    if (projectTasks.length === 0) return;
    const total = projectTasks.length;
    const completed = projectTasks.filter(t => t.status === "Completed").length;
    const newProgress = Math.round((completed / total) * 100);
    updateProject(projectId, { progress: newProgress }, true);
  };

  return (
    <TaskContext.Provider
      value={{ tasks, counts, loading, addTask, updateTask, deleteTask, getTaskById, fetchTaskById, fetchTasks, fetchTaskFilters, fetchTaskFormOptions }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => useContext(TaskContext);